import {
    createSlice, createAsyncThunk,
    PayloadAction,
    isPending,
    isRejectedWithValue,
    isFulfilled
} from '@reduxjs/toolkit';
import {
    ApiViewEntityResponse,
    ApiViewResponseError,
    ApplicationModel,
    CurrentUserModel,
    Message,
    MessageLevel,
    UserPasswordModel,
    RedirectErrorModel,
    UserAddressModel,
    UserContactDataModel,
    UserModel,
    UserPersonalDetailsModel
} from '../interfaces'
import { getBodyErrors } from '../services/apiService';
import identityService from '../services/identityService';

interface CurrentUserState {
    data: CurrentUserModel | undefined | null,
    userApplications: ApplicationModel[],
    userApplication: ApplicationModel | null,
    redirectError: RedirectErrorModel | null,
    pending: boolean,
    snack: string | null,
    messages: Message[]
}

const initialState: CurrentUserState = {
    data: undefined,
    userApplications: [],
    userApplication: null,
    redirectError: null,
    pending: false,
    snack: null,
    messages: []
}

const currentUserSlice = createSlice({
    name: 'currentUser',
    initialState,
    reducers: {
        initRedirectError: (state, action) => {
            state.redirectError = action.payload;
        },
        currentUserUnloaded: (state) => {
            state.data = undefined;
        },
        snackDisplayed: (state, action: PayloadAction<string | null>) => {
            state.snack = action.payload;
        },
        messagesDisplayed: (state, action: PayloadAction<Message[]>) => {
            state.messages = action.payload;
        },
        userNameUpdated: (state, action: PayloadAction<string | null>) => {
            if (state.data && action.payload !== null) {
                state.data = { ...state.data, userName: action.payload };
            }
        },
        userApplicationSelected: (state, action: PayloadAction<ApplicationModel>) => {
            state.userApplication = action.payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(getCurrentUser.fulfilled, (state, action) => {
                state.data = action.payload.data;
                state.pending = false;
            })
            .addCase(getCurrentUser.pending, (state) => {
                state.pending = true;
            })
            .addCase(getCurrentUser.rejected, (state) => {
                state.data = null;
                state.pending = false;
            })
            .addCase(getCurrentUserApplications.fulfilled, (state, action) => {
                state.userApplications = action.payload.data;
                state.pending = false;
            })
            .addCase(getCurrentUserApplications.pending, (state) => {
                state.pending = true;
            })
            .addCase(getCurrentUserApplications.rejected, (state) => {
                state.userApplications = [];
                state.pending = false;
            })
            .addCase(createUserAddress.fulfilled, (state, action) => {
                if (state.data !== null && state.data !== undefined) {
                    state.data = { ...state.data, userAddresses: action.payload.data.userAddresses };
                }
                state.pending = false;
                state.messages = [];
                state.snack = "Record Saved.";
            })
            .addCase(updateUserAddress.fulfilled, (state, action) => {
                if (state.data !== null && state.data !== undefined) {
                    state.data = { ...state.data, userAddresses: action.payload.data.userAddresses };
                }
                state.pending = false;
                state.messages = [];
                state.snack = "Record Saved.";
            })
            .addCase(createUserContact.fulfilled, (state, action) => {
                if (state.data !== null && state.data !== undefined) {
                    state.data = { ...state.data, contacts: action.payload.data.contacts };
                }
                state.pending = false;
                state.messages = [];
                state.snack = "Record Saved.";
            })
            .addCase(updateUserContact.fulfilled, (state, action) => {
                if (state.data !== null && state.data !== undefined) {
                    state.data = { ...state.data, contacts: action.payload.data.contacts };
                }
                state.pending = false;
                state.messages = [];
                state.snack = "Record Saved.";
            })
            .addCase(deleteUserContact.fulfilled, (state, action) => {
                if (state.data !== null && state.data !== undefined) {
                    state.data = { ...state.data, contacts: action.payload.data.contacts };
                }
                state.pending = false;
                state.messages = [];
                state.snack = "Record Saved.";
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.pending = false;
                if (action.payload?.some(e => e.detail == "Incorrect password.")) {
                    state.messages = [{ details: "Incorrect current password.", level: MessageLevel.Error }];
                }
                else {
                    state.messages = action.payload!.map(e => ({ details: e.detail, level: MessageLevel.Error }));
                }
            })
            .addMatcher(isApiFulfilledWithoutSnack, (state, action) => {
                state.pending = false;
                state.messages = [];
                state.snack = null;
            })
            .addMatcher(isApiFulfilledWithSnack, (state, action) => {
                state.pending = false;
                state.messages = [];
                state.snack = "Record Saved.";
            })
            .addMatcher(isApiPending, (state) => {
                state.pending = true;
            })
            .addMatcher(isApiRejected, (state, action) => {
                state.pending = false;
                state.snack = null;
                if (action.payload) {
                    state.messages = action.payload.map((e: { detail: any; }) => ({ details: e.detail, level: MessageLevel.Error }));
                }
            })
    }
});

export const getCurrentUser = createAsyncThunk<ApiViewEntityResponse<CurrentUserModel>, undefined, { rejectValue: ApiViewResponseError[] }>
    ('user/current/get', async (_, { rejectWithValue }) => {
        try {
            const response = await identityService.getCurrentUser();
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const getCurrentUserApplications = createAsyncThunk<ApiViewEntityResponse<ApplicationModel[]>, undefined, { rejectValue: ApiViewResponseError[] }>
    ('user/current/applications/get', async (_, { rejectWithValue }) => {
        try {
            const response = await identityService.getCurrentUserApplications();
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserPersonalDetails = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserPersonalDetailsModel, { rejectValue: ApiViewResponseError[] }>
    ('user/current/personal-details/update', async (request: UserPersonalDetailsModel, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserPersonalDetails(request);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserName = createAsyncThunk<ApiViewEntityResponse<UserModel>, string, { rejectValue: ApiViewResponseError[] }>
    ('user/current/update/userName', async (userName: string, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserName(userName);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserAddress = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserAddressModel, { rejectValue: ApiViewResponseError[] }>
    ('user/current/address/create', async (request: UserAddressModel, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserAddress(request);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserAddress = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserAddressRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/current/address/update', async (request: UserAddressRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserAddress(request.userAddressId, request.userAddress);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const createUserContact = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserContactDataModel, { rejectValue: ApiViewResponseError[] }>
    ('user/current/contact/create', async (request: UserContactDataModel, { rejectWithValue }) => {
        try {
            const response = await identityService.createUserContact(request);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const updateUserContact = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserContactRequest, { rejectValue: ApiViewResponseError[] }>
    ('user/current/contact/update', async (request: UserContactRequest, { rejectWithValue }) => {
        try {
            const response = await identityService.updateUserContact(request.contactId, request.contact);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const deleteUserContact = createAsyncThunk<ApiViewEntityResponse<UserModel>, number, { rejectValue: ApiViewResponseError[] }>
    ('user/current/contact/delete', async (contactId: number, { rejectWithValue }) => {
        try {
            const response = await identityService.deleteUserContact(contactId);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });

export const changePassword = createAsyncThunk<ApiViewEntityResponse<UserModel>, UserPasswordModel, { rejectValue: ApiViewResponseError[] }>
    ('user/current/update/password', async (request: UserPasswordModel, { rejectWithValue }) => {
        try {
            const response = await identityService.changeUserPassword(request);
            return response;
        } catch (err: any) {
            return rejectWithValue(await getBodyErrors(err));
        }
    });


const isApiPending = isPending(
    updateUserPersonalDetails,
    updateUserName,
    createUserAddress,
    updateUserAddress,
    createUserContact,
    updateUserContact,
    deleteUserContact,
    changePassword
);
const isApiRejected = isRejectedWithValue(
    updateUserPersonalDetails,
    updateUserName,
    createUserAddress,
    updateUserAddress,
    createUserContact,
    updateUserContact,
    deleteUserContact
);
const isApiFulfilledWithSnack = isFulfilled(
    updateUserPersonalDetails,
    changePassword
);
const isApiFulfilledWithoutSnack = isFulfilled(updateUserName);

export const {
    initRedirectError,
    currentUserUnloaded,
    snackDisplayed,
    messagesDisplayed,
    userNameUpdated,
    userApplicationSelected
} = currentUserSlice.actions;
export default currentUserSlice.reducer

export interface UserAddressRequest {
    userAddressId: number,
    userAddress: UserAddressModel
}

export interface UserContactRequest {
    contactId: number,
    contact: UserContactDataModel
}
